<template>
    <nav class="navbar navbar-expand-lg">
        <div class="container-fluid">
            <div class="left-section">
                <button
                    class="btn btn-icon btn-round navbar-burger"
                    @click="$emit('toggle-sidebar')"
                >
                    <i class="fa fa-bars"></i>
                </button>
            </div>
            <div class="center-section">
                <label class="navbar-brand">
                    {{ systemTitle }} / {{ pageTitle }}
                </label>
            </div>
            <div class="right-section">
                <div class="toolbar-group">
                    <LanguageSelector
                        :languages="languages"
                        :currentLanguage="currentLanguage"
                        @language-changed="changeLanguage"
                    />
                </div>
                <div class="user-menu-wrapper">
                    <button
                        ref="toggleButtonRef"
                        class="btn btn-icon btn-round"
                        @click="toggleUserMenu"
                    >
                        <i class="fa fa-user"></i>
                    </button>
                    <div
                        ref="userMenuRef"
                        v-show="isUserMenuOpen"
                        class="user-menu-dropdown"
                    >
                        <div class="user-info">
                            <p>{{ welcomeMessage }}</p>
                            <p>
                                {{
                                    `${$t('users.users_fld__name')}: ${
                                        userSelf.username
                                    }`
                                }}
                            </p>
                            <p>
                                {{
                                    `${$t('users.users_fld__club_name')}: ${
                                        userSelf.club_name
                                    }`
                                }}
                            </p>
                            <p>
                                {{
                                    `${$t(
                                        'users.users_fld__stations__name'
                                    )}: ${
                                        userSelf.station_id_list?.join(', ') ||
                                        userSelf.station
                                    }`
                                }}
                            </p>
                        </div>
                        <div class="menu-actions nav-items">
                            <button
                                class="btn btn-link"
                                @click="changePassword"
                            >
                                <i class="ti-key"></i>
                                {{ $t('sidebar.sidebar_msg__change_password') }}
                            </button>
                            <div class="timezone-control">
                                <label class="timezone-label">
                                    {{
                                        $t(
                                            'sidebar.navbar_msg__timezone_placeholder'
                                        ) || 'Time Zone'
                                    }}
                                </label>
                                <input
                                    v-model="timeZoneQuery"
                                    class="timezone-search-select"
                                    :list="timeZoneDataListId"
                                    type="text"
                                    :placeholder="
                                        $t(
                                            'sidebar.navbar_msg__timezone_search_placeholder'
                                        ) || 'Search or select time zone'
                                    "
                                    :disabled="isUpdatingTimeZone"
                                    @change="handleTimeZoneInputChange"
                                    @keyup.enter.prevent="
                                        handleTimeZoneInputChange
                                    "
                                />
                                <datalist :id="timeZoneDataListId">
                                    <option
                                        v-for="tz in filteredTimeZoneOptions"
                                        :key="tz"
                                        :value="tz"
                                    ></option>
                                </datalist>
                            </div>
                            <button class="btn btn-link" @click="logout">
                                <i class="fa fa-sign-out"></i>
                                {{ $t('login.login_msg__sign_out') }}
                            </button>
                            <hr style="border-color: black" />
                            <label class="navbar-brand">
                                Version: {{ version_display }}
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </nav>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import LanguageSelector from '@/components/Navbar/LanguageSelector.vue';
import { useLocale } from '@/composables/useLocale';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { version } from '@/version';
import { updateUserTimeZone } from '@/api/users';
import { getTimeZoneOffsetMinutes, resolveTimeZone } from '@/utils/timezone';

export default {
    name: 'NavbarComponent',
    components: {
        LanguageSelector,
    },
    emits: ['toggle-sidebar'],
    props: {
        systemTitle: {
            type: String,
            default: 'Club Assist',
        },
        pageTitle: {
            type: String,
            default: '',
        },
        userSelf: {
            type: Object,
            default: () => ({
                username: '',
                club_name: '',
            }),
        },
        welcomeMessage: {
            type: String,
            default: 'Welcome!',
        },
    },
    setup(props) {
        const { currentLanguage, languages, changeLanguage, fetchLanguages } =
            useLocale();
        const router = useRouter();
        const store = useStore();

        const isUserMenuOpen = ref(false);

        const userMenuRef = ref(null);
        const toggleButtonRef = ref(null);

        const version_display = version;
        const isUpdatingTimeZone = ref(false);

        const getSupportedTimeZones = () => {
            if (typeof Intl?.supportedValuesOf === 'function') {
                try {
                    const list = Intl.supportedValuesOf('timeZone');
                    if (Array.isArray(list) && list.length) {
                        return list;
                    }
                } catch (error) {
                    console.warn('supportedValuesOf not available', error);
                }
            }
            return ['UTC'];
        };

        const timeZoneOptions = ref(getSupportedTimeZones());
        const timeZoneLabelCache = new Map();
        const getTimeZoneLabel = tz => {
            if (!tz) return '';
            if (timeZoneLabelCache.has(tz)) {
                return timeZoneLabelCache.get(tz);
            }
            const offsetMinutesRaw = getTimeZoneOffsetMinutes(Date.now(), tz);
            const offsetMinutes = Math.round(offsetMinutesRaw);
            const sign = offsetMinutes >= 0 ? '+' : '-';
            const absMinutes = Math.abs(offsetMinutes);
            const hours = String(Math.floor(absMinutes / 60)).padStart(2, '0');
            const minutes = String(absMinutes % 60).padStart(2, '0');
            const label = `${tz} (UTC${sign}${hours}:${minutes})`;
            timeZoneLabelCache.set(tz, label);
            return label;
        };
        const timeZoneDisplayOptions = computed(() =>
            timeZoneOptions.value.map(tz => ({
                tz,
                label: getTimeZoneLabel(tz),
                tzLower: tz.toLowerCase(),
                labelLower: getTimeZoneLabel(tz).toLowerCase(),
            }))
        );
        const selectedTimeZone = ref(
            resolveTimeZone(
                props.userSelf?.time_zone ||
                    props.userSelf?.timeZone ||
                    props.userSelf?.timezone ||
                    null
            )
        );
        const timeZoneQuery = ref(getTimeZoneLabel(selectedTimeZone.value));
        const timeZoneDataListId = 'timezone-options';

        const fuzzyMatchScore = (query, candidate) => {
            if (!query) return 0;
            let score = 0;
            let lastIndex = -1;
            for (const char of query) {
                const idx = candidate.indexOf(char, lastIndex + 1);
                if (idx === -1) return -Infinity;
                score += idx === lastIndex + 1 ? 2 : 1;
                lastIndex = idx;
            }
            return score;
        };

        const filteredTimeZoneOptions = computed(() => {
            const query = timeZoneQuery.value.trim().toLowerCase();
            if (!query) {
                const base = timeZoneDisplayOptions.value.map(
                    item => item.label
                );
                const currentLabel = getTimeZoneLabel(selectedTimeZone.value);
                if (currentLabel && !base.includes(currentLabel)) {
                    base.unshift(currentLabel);
                }
                return base;
            }
            const scored = timeZoneDisplayOptions.value
                .map(item => ({
                    label: item.label,
                    score: Math.max(
                        fuzzyMatchScore(query, item.tzLower),
                        fuzzyMatchScore(query, item.labelLower)
                    ),
                }))
                .filter(
                    item =>
                        Number.isFinite(item.score) && item.score > -Infinity
                )
                .sort(
                    (a, b) =>
                        b.score - a.score || a.label.localeCompare(b.label)
                )
                .map(item => item.label);
            const currentLabel = getTimeZoneLabel(selectedTimeZone.value);
            if (currentLabel && !scored.includes(currentLabel)) {
                scored.unshift(currentLabel);
            }
            return scored;
        });

        const toggleUserMenu = () => {
            isUserMenuOpen.value = !isUserMenuOpen.value;
        };
        const changePassword = () => {
            router.push('/users/change-password');
        };

        const logout = () => {
            store.dispatch('auth/signout');
            router.push('/');
        };

        const syncSelectedTimeZone = () => {
            selectedTimeZone.value = resolveTimeZone(
                props.userSelf?.time_zone ||
                    props.userSelf?.timeZone ||
                    props.userSelf?.timezone ||
                    null
            );
            timeZoneQuery.value = getTimeZoneLabel(selectedTimeZone.value);
        };

        const handleTimeZoneChange = async () => {
            const next = selectedTimeZone.value;
            if (!next) return;
            if (
                resolveTimeZone(
                    props.userSelf?.time_zone ||
                        props.userSelf?.timeZone ||
                        props.userSelf?.timezone ||
                        null
                ) === next
            ) {
                return;
            }

            try {
                isUpdatingTimeZone.value = true;
                const result = await updateUserTimeZone(next);
                const nextUser = (result && result.user_data) || {
                    ...(props.userSelf || {}),
                    time_zone: next,
                };
                store.dispatch('auth/updateUserSelf', nextUser);
            } catch (error) {
                console.error('Failed to change time zone', error);
                // Roll back selection if API fails
                syncSelectedTimeZone();
            } finally {
                isUpdatingTimeZone.value = false;
                // force reload to reflect time zone change across the app
                window.location.reload();
            }
        };

        const handleTimeZoneInputChange = () => {
            const query = timeZoneQuery.value.trim();
            if (!query) return;
            const queryLower = query.toLowerCase();
            const match = timeZoneDisplayOptions.value.find(
                item =>
                    item.labelLower === queryLower ||
                    item.tzLower === queryLower
            );
            if (!match) return;
            selectedTimeZone.value = match.tz;
            timeZoneQuery.value = match.label;
            handleTimeZoneChange();
        };

        const handleClickOutside = event => {
            if (isUserMenuOpen.value) {
                if (
                    userMenuRef.value &&
                    !userMenuRef.value.contains(event.target) &&
                    toggleButtonRef.value &&
                    !toggleButtonRef.value.contains(event.target)
                ) {
                    isUserMenuOpen.value = false;
                }
            }
        };

        onMounted(() => {
            fetchLanguages();
            document.addEventListener('click', handleClickOutside);
        });

        onUnmounted(() => {
            document.removeEventListener('click', handleClickOutside);
        });

        watch(
            () => props.userSelf,
            () => {
                syncSelectedTimeZone();
            },
            { deep: true }
        );

        return {
            currentLanguage,
            languages,
            changeLanguage,
            isUserMenuOpen,
            toggleUserMenu,
            changePassword,
            logout,
            userMenuRef,
            toggleButtonRef,
            version_display,
            timeZoneOptions,
            selectedTimeZone,
            filteredTimeZoneOptions,
            timeZoneQuery,
            timeZoneDataListId,
            handleTimeZoneChange,
            handleTimeZoneInputChange,
            isUpdatingTimeZone,
        };
    },
};
</script>

<style scoped>
.navbar {
    width: 100%;
    /* height: 60px; */
    background-color: #333;
    color: white;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-sizing: border-box;
}
.toolbar-group {
    display: flex;
    align-items: center;
    gap: 8px;
}
.timezone-control {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 200px;
}
.timezone-label {
    font-size: 12px;
    color: #ddd;
}
.timezone-search-select {
    padding: 6px 10px;
    border: 1px solid #ccc;
    border-radius: 5px;
    background-color: #fff;
    color: #333;
    font-size: 14px;
    outline: none;
    transition: border-color 0.3s ease;
}
.timezone-search-select:focus {
    border-color: #007bff;
}
input[type='checkbox']:checked ~ #sidebarMenu {
    transform: translateX(0);
}

input[type='checkbox'] {
    transition: all 0.3s;
    box-sizing: border-box;
    display: none;
}
.sidebarIconToggle {
    transition: all 0.3s;
    box-sizing: border-box;
    cursor: pointer;
    position: absolute;
    z-index: 99;
    height: 100%;
    width: 100%;
    top: 22px;
    left: 15px;
    height: 22px;
    width: 22px;
}
.spinner {
    transition: all 0.3s;
    box-sizing: border-box;
    position: absolute;
    height: 3px;
    width: 100%;
    background-color: #fff;
}
.horizontal {
    transition: all 0.3s;
    box-sizing: border-box;
    position: relative;
    float: left;
    margin-top: 3px;
}
.diagonal.part-1 {
    position: relative;
    transition: all 0.3s;
    box-sizing: border-box;
    float: left;
}
.diagonal.part-2 {
    transition: all 0.3s;
    box-sizing: border-box;
    position: relative;
    float: left;
    margin-top: 3px;
}
input[type='checkbox']:checked ~ .sidebarIconToggle > .horizontal {
    transition: all 0.3s;
    box-sizing: border-box;
    opacity: 0;
}
input[type='checkbox']:checked ~ .sidebarIconToggle > .diagonal.part-1 {
    transition: all 0.3s;
    box-sizing: border-box;
    transform: rotate(135deg);
    margin-top: 8px;
}
input[type='checkbox']:checked ~ .sidebarIconToggle > .diagonal.part-2 {
    transition: all 0.3s;
    box-sizing: border-box;
    transform: rotate(-135deg);
    margin-top: -9px;
}
.left-section,
.center-section,
.right-section {
    display: flex;
    align-items: center;
}

.right-section {
    display: flex;
    align-items: center;
}

.language-selector {
    margin-right: 15px;
}

.language-selector select {
    padding: 5px 10px;
    border: 1px solid #ccc;
    border-radius: 5px;
    background-color: #fff;
    color: #333;
    font-size: 14px;
    outline: none;
    transition: border-color 0.3s ease;
}

.language-selector select:focus {
    border-color: #007bff;
}

.language-selector select option {
    background-color: #fff;
    color: #333;
}
.user-menu-wrapper {
    position: relative;
}

.user-menu-dropdown {
    position: absolute;
    right: 0;
    top: 100%;
    background-color: #fff;
    color: #333;
    border: 1px solid #ccc;
    border-radius: 5px;
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
    padding: 10px;
    width: 250px;
    z-index: 1000;
}

.user-info p {
    margin: 0;
    padding: 5px 0;
    font-weight: bold;
    word-wrap: break-word;
    text-align: left;
}

.menu-actions {
    margin-top: 10px;
}

.menu-actions .btn-link {
    display: block;
    width: 100%;
    text-align: left;
    padding: 5px 0;
    color: #7a9e9f;
    text-decoration: none;
}

.menu-actions .btn-link:hover {
    text-decoration: none;
}

@media (max-width: 991px) {
    .navbar {
        align-items: stretch;
        padding: 16px;
        min-height: 160px;
    }

    .navbar .container-fluid {
        display: grid;
        grid-template-columns: auto 1fr;
        grid-template-areas:
            'left center'
            'right right';
        align-items: center;
        row-gap: 12px;
    }

    .left-section,
    .center-section,
    .right-section {
        width: 100%;
    }
    .left-section {
        grid-area: left;
        justify-content: flex-start;
    }
    .center-section,
    .right-section {
        justify-content: flex-end;
        text-align: right;
    }
    .center-section {
        grid-area: center;
    }
    .right-section {
        grid-area: right;
    }

    .center-section .navbar-brand {
        white-space: normal;
        line-height: 1.4;
    }

    .right-section {
        flex-direction: column;
        align-items: flex-end;
        gap: 8px;
    }

    .user-menu-wrapper {
        width: 100%;
    }

    .user-menu-dropdown {
        right: auto;
        left: 0;
        width: 100%;
        max-width: 320px;
    }
}
</style>
